import { useUser } from "@/context/useUser";
import { getTenantId } from "@/lib/storeManager";
import LoginFormSchema from "@/schemas/LoginFromSchema";
import SocialConfigurationService from "@/services/APIs/SocialConfigurationService";
import navigationRoutes from "@/utils/navigationRoutes";
import { FormItem, Input, Password, Submit } from "@formily/antd-v5";
import { createForm } from "@formily/core";
import { FormProvider, createSchemaField } from "@formily/react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const form = createForm({});

const SchemaField = createSchemaField({
  components: { FormItem, Input, Password },
});

const LoginForm: React.FC = () => {
  const { login, isAuthenticated, socialLogin } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const tenantId = getTenantId() ?? "";

  const handleSubmit = async (values: { email: string; password: string }) => {
    setLoading(true);
    const response = await login(values.email, values.password);
    if (response) {
      navigate(navigationRoutes.SCHEMAS);
    }
    setLoading(false);
  };

  const { data } = useQuery({
    queryKey: ["socialConfigurations", tenantId],
    queryFn: () => SocialConfigurationService.listSocialConfigurations(1, 10),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  // console.log(data);

  const providerStyles: Record<string, string> = {
    google: "bg-blue-500",
    github: "bg-gray-800",
    apple: "bg-black",
    facebook: "bg-blue-700",
    linkedin: "bg-blue-800",
    twitter: "bg-blue-900",
    instagram: "bg-black",
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(navigationRoutes.SCHEMAS);
    }
  }, [isAuthenticated, tenantId, navigate]);

  const handleOAuthLogin = async (provider: string) => {
    try {
      const response = await socialLogin(provider);
      if (response?.auth_url) {
        window.location.href = response.auth_url;
      } else {
        console.error("OAuth login failed: no auth_url returned", response);
      }
    } catch (err) {
      console.error("Error during OAuth login", err);
    }
  };

  return (
    <Card title="Login" className="w-full max-w-md rounded-xl shadow-xl">
      <FormProvider form={form}>
        <SchemaField schema={LoginFormSchema} />
        <Submit
          onSubmit={handleSubmit}
          disabled={loading}
          className="w-full rounded-md px-4 py-2 text-white"
        >
          {loading ? "Logging in…" : "Login"}
        </Submit>
      </FormProvider>

      {/* Static OAuth buttons */}
      <div className="mt-6 space-y-2">
        {data?.data?.map((config: any) => {
          const provider = config.provider.toLowerCase();
          const style = providerStyles[provider] ?? "bg-gray-500"; // default color
          const label = provider.charAt(0).toUpperCase() + provider.slice(1);

          return (
            <button
              key={config.id}
              onClick={() => handleOAuthLogin(provider)}
              className={`w-full rounded-md px-4 py-2 text-white ${style}`}
            >
              Login with {label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <span className="text-sm text-gray-500">
            {"Don't have an account?"}
          </span>
          <Link to={navigationRoutes.SIGNUP} className="text-blue-500">
            Signup
          </Link>
          {/* Forgot Password Link */}
          <div className="mt-2">
            <Link
              to={navigationRoutes.FORGOT_PASSWORD}
              className="text-sm text-blue-500"
            >
              Forgot Password?
            </Link>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default LoginForm;
